import {createAsyncThunk} from "@reduxjs/toolkit";
import {endOfDay, getUnixTime, startOfDay} from "date-fns";
import {getData} from "../../Api/api";
import {
    EP_PRODUCTS_INSIGHTS_SELECTED,
    EP_PRODUCTS_INSIGHTS_SOLD,
    EP_PRODUCTS_PERFORMANCES,
    EP_PRODUCTS_STATS
} from "../../Api/endPoints";
import {chartArrayMapper} from "../../utility/Utils";
import {mappedProductDetail} from "./product.mapper";
import { zonedTimeToUtc } from "date-fns-tz";

const buildBaseParams = ({ startDate, endDate, brand, skip = 0, limit = 10, order_by, sort = "DESC" }) => ({
    start: getUnixTime(zonedTimeToUtc(startOfDay(startDate), "GMT")),
    end: getUnixTime(zonedTimeToUtc(endOfDay(endDate), "GMT")),
    brand_id: brand,
    count: limit,
    offset: skip,
    ...(order_by ? { order_by } : {}),
    sort,
    timezone: new Date().getTimezoneOffset()
});

export const getProductStats = createAsyncThunk(
    "product/getProductStats",
    async (
        {startDate, endDate, brand = 0, language = null},
        thunkAPI
    ) => {
        try {
            const params = buildBaseParams({ startDate, endDate, brand, skip: 0, limit: 0, sort: "DESC" });

            const {
                products_sold_per_subcategories: productsSoldPerSubcategories,
                products_sold_per_colors: productsSoldPerColors,
                products_sold_per_period: productsSoldPerPeriod,
            } = await getData(EP_PRODUCTS_STATS, {params});

            const {productStatsData} = thunkAPI.getState().product;

            const paginatedProductsSoldPerColors = productsSoldPerColors?.sort((a, b) => b.percent - a.percent).slice(0, 6) ?? [];// get last 6 element order by descending percent
            let paginatedProductsSoldPerSubcategories = productsSoldPerSubcategories?.sort((a, b) => b.percent - a.percent).slice(-6) || [];

            return {
                ...productStatsData,
                charts: {
                    ...productStatsData.charts,
                    saleWithColorDetail: chartArrayMapper(paginatedProductsSoldPerColors, `name_${language}`, 'percent', '', '', 'color', 'turnover'),
                    categoriesPercent: chartArrayMapper(paginatedProductsSoldPerSubcategories, `text-${language}`, 'percent', '', ''),
                    categoriesTotalSales: chartArrayMapper(paginatedProductsSoldPerSubcategories, `text-${language}`, 'turnover', '', ''),
                    productDetail: mappedProductDetail(productsSoldPerPeriod, productsSoldPerPeriod?.[0]?.type ?? '', startDate)
                }
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);

export const getSelectedProductsTable = createAsyncThunk(
    "product/getSelectedProductsTable",
    async (
        {startDate, endDate, brand = 0, skip = 0, limit = 10, order_by = "shared_clicks", sort = "DESC"},
        thunkAPI
    ) => {
        try {
            const params = buildBaseParams({ startDate, endDate, brand, skip, limit, order_by, sort });
            const { shared_clicked_products: rows = [], total_count: totalCount = 0 } = await getData(EP_PRODUCTS_INSIGHTS_SELECTED, { params });
            return {
                tablesData: {
                    selectedProductTableData: rows,
                },
                selectedTableTotalRows: Number(totalCount) || rows.length || 0,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);

export const getSoldProductsTable = createAsyncThunk(
    "product/getSoldProductsTable",
    async (
        {startDate, endDate, brand = 0, skip = 0, limit = 10, order_by = "selled_products", sort = "DESC"},
        thunkAPI
    ) => {
        try {
            const params = buildBaseParams({ startDate, endDate, brand, skip, limit, order_by, sort });
            const { products_sold: rows = [], total_count: totalCount = 0 } = await getData(EP_PRODUCTS_INSIGHTS_SOLD, { params });
            return {
                tablesData: {
                    saledProductTableData: rows,
                },
                soldTableTotalRows: Number(totalCount) || rows.length || 0,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);

export const getProductPerformances = createAsyncThunk(
    "product/getProductPerformances",
    async ({startDate, endDate, brand = 0}, thunkAPI) => {
        try {
            const params = {
                start: getUnixTime(zonedTimeToUtc(startOfDay(startDate),"GMT")),
                end: getUnixTime(zonedTimeToUtc(endOfDay(endDate),"GMT")),
                brand_id: brand,
                // count: 20,
                timezone: new Date().getTimezoneOffset()
            };

            const {
                shared_products_clicks: sharedProductsClicks,
                products_sold: productsSold
            } = await getData(EP_PRODUCTS_PERFORMANCES, {params});

            return {
                global: {
                    sharedProductsClicks: sharedProductsClicks ? sharedProductsClicks : 0,
                    productsSold: productsSold ? productsSold : 0,
                    sharedProductsClicksNotSolded: sharedProductsClicks && productsSold ? Math.abs(sharedProductsClicks - productsSold) : 0
                }
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);
